/**
 * CertPremiumActions — premium-tier button row for any TSOGN certificate.
 *
 * Adds three actions next to the existing free PDF + verify link:
 *
 *   1. Print-Ready PDF (A3 landscape, 300 DPI, QR-coded, frame-quality)
 *   2. Share image (1200×630 OpenGraph PNG with QR for tweets / LinkedIn)
 *   3. Order physical print (waitlist form — Stripe billing opens once
 *      LLC + domain land; until then we collect email + intent so day-1
 *      we can email the queue).
 *
 * Apr 29 2026 — Lead asked to ship this as the first item in the
 * post-globe-lock batch. The data model was already there; this
 * component is the user-visible surface.
 */
import { useState } from 'react';
import { Printer, Image as ImageIcon, Mail, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { apiBase } from '../lib/absoluteMediaUrl';

export default function CertPremiumActions({ certificate, testIdPrefix = 'cert' }) {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitForm, setWaitForm] = useState({ email: '', name: '', note: '' });
  const [waitState, setWaitState] = useState('idle'); // idle | submitting | sent | error
  const [waitError, setWaitError] = useState('');

  if (!certificate?.certificate_id) return null;
  const cid = certificate.certificate_id;
  const _base = apiBase();
  const printUrl = `${_base}/api/certificates/${cid}/print.pdf`;
  const shareUrl = `${_base}/api/certificates/${cid}/share.png`;

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!waitForm.email) {
      setWaitState('error');
      setWaitError('Email is required.');
      return;
    }
    setWaitState('submitting');
    setWaitError('');
    try {
      await api.post(`/certificates/${cid}/print/waitlist`, waitForm);
      setWaitState('sent');
    } catch (err) {
      setWaitState('error');
      setWaitError(err.response?.data?.detail || 'Could not join the waitlist. Try again.');
    }
  };

  return (
    <div className="mt-3" data-testid={`${testIdPrefix}-premium-actions`}>
      <div className="flex flex-wrap gap-2">
        <a
          href={printUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testIdPrefix}-print-pdf-link`}
          className="font-hud text-xs tracking-widest text-[#D4A84A] border border-[#D4A84A]/55 bg-[#D4A84A]/10 hover:bg-[#D4A84A]/20 px-3 py-2 inline-flex items-center gap-1.5 transition-colors"
          title="A3 landscape, 300 DPI, QR-coded — frame-ready"
        >
          <Printer size={12} /> PRINT-READY PDF
        </a>
        <a
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testIdPrefix}-share-png-link`}
          className="font-hud text-xs tracking-widest text-[#2F8A92] border border-[#2F8A92]/40 hover:bg-[#2F8A92]/10 px-3 py-2 inline-flex items-center gap-1.5 transition-colors"
          title="1200×630 OpenGraph share card with QR — drop into a tweet or LinkedIn"
        >
          <ImageIcon size={12} /> SHARE IMAGE
        </a>
        <button
          onClick={() => setWaitlistOpen((s) => !s)}
          data-testid={`${testIdPrefix}-waitlist-toggle`}
          className="font-hud text-xs tracking-widest text-white/85 border border-white/20 hover:border-[#D4A84A]/55 hover:text-[#D4A84A] px-3 py-2 inline-flex items-center gap-1.5 transition-colors"
          title="Get notified when physical-print mailing opens"
        >
          <Mail size={12} /> ORDER PHYSICAL PRINT
        </button>
      </div>

      {waitlistOpen && (
        <div className="mt-3 border border-[#D4A84A]/25 bg-[#D4A84A]/5 rounded p-4" data-testid={`${testIdPrefix}-waitlist-panel`}>
          <p className="font-hud text-[10px] md:text-xs tracking-[0.18em] uppercase text-[#D4A84A]/85 mb-1.5">⟨ Physical-print mailing — waitlist ⟩</p>
          <p className="font-mono text-xs text-white/80 mb-3 leading-relaxed">
            Stripe billing opens after LLC paperwork clears. Drop your email and we'll notify you the day fulfillment goes live — frame-quality A3 print, hand-signed, mailed worldwide. Backers get
            a 30-day priority window before we open it to the public.
          </p>

          {waitState === 'sent' ? (
            <div className="flex items-center gap-2 text-[#2F8A92] font-mono text-xs" data-testid={`${testIdPrefix}-waitlist-success`}>
              <CheckCircle2 size={14} /> You're on the list. We'll write the day fulfillment opens.
            </div>
          ) : (
            <form onSubmit={submitWaitlist} className="space-y-2.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <input
                  type="email"
                  required
                  placeholder="email (required)"
                  value={waitForm.email}
                  onChange={(e) => setWaitForm({ ...waitForm, email: e.target.value })}
                  data-testid={`${testIdPrefix}-waitlist-email`}
                  className="bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 font-mono text-sm text-white focus:border-[#D4A84A]/55 focus:outline-none transition-colors"
                />
                <input
                  type="text"
                  placeholder="name (optional)"
                  value={waitForm.name}
                  onChange={(e) => setWaitForm({ ...waitForm, name: e.target.value })}
                  data-testid={`${testIdPrefix}-waitlist-name`}
                  className="bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 font-mono text-sm text-white focus:border-[#D4A84A]/55 focus:outline-none transition-colors"
                />
              </div>
              <textarea
                rows={2}
                placeholder="note (optional) — gift recipient address, frame size preference, etc."
                value={waitForm.note}
                onChange={(e) => setWaitForm({ ...waitForm, note: e.target.value })}
                data-testid={`${testIdPrefix}-waitlist-note`}
                className="w-full bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 font-mono text-xs text-white focus:border-[#D4A84A]/55 focus:outline-none transition-colors resize-y"
              />
              {waitState === 'error' && (
                <div className="flex items-center gap-1.5 text-red-300 font-mono text-xs" data-testid={`${testIdPrefix}-waitlist-error`}>
                  <AlertCircle size={12} /> {waitError}
                </div>
              )}
              <button
                type="submit"
                disabled={waitState === 'submitting'}
                data-testid={`${testIdPrefix}-waitlist-submit`}
                className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] hover:bg-[#D4A84A]/85 px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
              >
                {waitState === 'submitting' ? <><Loader2 size={12} className="animate-spin" /> SUBMITTING…</> : <><Mail size={12} /> JOIN WAITLIST</>}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
